import React, { useCallback, useEffect } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Divider, Input, Row, Select, Space, Spin, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import EditableField from 'src/components/fields/EditableField';
import { DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { useUserById } from 'src/features/users/users.hooks';
import { deleteUser, fetchUser, updateUser } from 'src/features/users/users.state';
import { RoleEnum, User } from 'src/features/users/users.types';

const CmsUserPage: React.FC = () => {
  const params = useParams() as { userId: string };
  const userId = Number(params.userId);
  const dispatch = useDispatch();
  const user = useUserById(Number(userId)) as User;

  const handleDeleteButtonClick = useCallback(() => {
    dispatch(deleteUser({ userId }));
  }, [dispatch, userId]);

  const handleUserUpdate = useCallback(
    (data: Partial<User>) => {
      dispatch(updateUser({ userId, data }));
    },
    [dispatch, userId]
  );

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/settings/users'>
                <Space>
                  <UserOutlined />
                  Пользователи CMS
                </Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            {user.username} ({user.email})
          </Typography.Title>
        </div>
        <div className={styles.sidebar}>
          <Space>
            <Button type='primary' onClick={() => handleUserUpdate({ blocked: !user.blocked })} danger={!user.blocked}>
              {user.blocked ? 'Разблокировать' : 'Заблокировать'}
            </Button>
          </Space>
          <div className={styles.propertiesGrid}>
            <Typography.Text className={styles.title}>Почта</Typography.Text>
            <EditableField value={user.email}>
              <Input
                type='email'
                name='email'
                value={user.email}
                placeholder='Введите e-mail'
                onChange={ev => handleUserUpdate({ email: ev.target.value })}
              />
            </EditableField>
            <Typography.Text className={styles.title}>Администратор</Typography.Text>
            <EditableField value={user.admin ? 'Да' : 'Нет'}>
              <Select
                value={user.admin ? 'Да' : 'Нет'}
                onSelect={e => handleUserUpdate({ admin: Number(e) === 1 })}
                style={{ width: '100%' }}
                defaultOpen
              >
                {[
                  { label: 'Да', value: 1 },
                  { label: 'Нет', value: 0 },
                ].map(l => (
                  <Select.Option key={l.label} value={l.value}>
                    {l.label}
                  </Select.Option>
                ))}
              </Select>
            </EditableField>
            <Typography.Text className={styles.title}>Пароль</Typography.Text>
            <EditableField value='Сменить пароль'>
              <Input
                type='password'
                name='password'
                placeholder='Введите пароль'
                onBlur={ev => handleUserUpdate({ password: ev.target.value })}
              />
            </EditableField>
            <Typography.Text className={styles.title}>Роль</Typography.Text>
            <EditableField value={user.role}>
              <Select
                value={user.role}
                onSelect={e => handleUserUpdate({ role: e })}
                style={{ width: '100%' }}
                defaultOpen
              >
                {[
                  { label: 'Администратор', value: RoleEnum.admin },
                  { label: 'Контент-менеджер', value: RoleEnum.contentManager },
                  { label: 'Модератор', value: RoleEnum.moderator },
                  { label: 'Наблюдатель', value: RoleEnum.observer },
                  { label: 'Менеджер отеля', value: RoleEnum.companyContentManager },
                ].map(l => (
                  <Select.Option key={l.label} value={l.value}>
                    {l.label}
                  </Select.Option>
                ))}
              </Select>
            </EditableField>
          </div>
          <Divider />
          <Space direction='vertical' size='small'>
            <Button type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
              <DeleteOutlined /> Удалить пользователя
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const CmsUserPageBlock: React.FC = () => {
  const { userId } = useParams() as { userId: string };
  const dispatch = useDispatch();
  const user = useUserById(Number(userId));

  useEffect(() => {
    dispatch(fetchUser({ userId: Number(userId) }));
  }, [dispatch, userId]);

  if (!user) {
    return (
      <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
        <Spin size={'large'} />
      </Row>
    );
  }

  return <CmsUserPage />;
};

export default CmsUserPageBlock;
